import type { AiGroup, AppBuild, Demo, Image, LinkRef } from './schema'
import { externalLinks } from './site'

export const workPage = {
  eyebrow: 'Work',
  title: 'Real builds you can open.',
  lede: 'Everything here shipped. Open any build at full size.',
}

/* ---------- GoHighLevel systems ---------- */

const shot = (file: string, alt: string, width: number, height: number): Image => ({
  src: `/images/work/${file}`,
  alt,
  width,
  height,
})

export const ticketingSystem = {
  kicker: 'GoHighLevel blueprint',
  title: 'Support Ticketing System',
  summary:
    'A full support desk built inside GoHighLevel. One pipeline, five stages, four branded emails, six workflows, and zero manual replies.',
  link: { label: 'Open the live blueprint', href: externalLinks.ticketing, external: true } satisfies LinkRef,
  cover: shot('ticketing-3.webp', 'The ticketing workflow in the GoHighLevel builder', 1600, 900),
  gallery: [
    shot('ticketing-overview.webp', 'Support ticketing system overview', 1616, 851),
    shot('ticketing-2.webp', 'Ticketing pipeline stages', 1600, 900),
    shot('ticketing-3.webp', 'Ticketing workflow in the builder', 1600, 900),
    shot('ticketing-4.webp', 'Ticket stage view', 1842, 920),
    shot('ticketing-5.webp', 'Ticket automation steps', 1269, 946),
    shot('ticketing-6.webp', 'Branded ticket email', 1804, 931),
    shot('ticketing-7.webp', 'Ticket board', 1641, 806),
  ],
}

export const buildMethod: { title: string; summary: string; link: LinkRef }[] = [
  {
    title: 'The Agentic Framework',
    summary: 'The tools, the rules, and the loop I use to ship apps with AI. The AI does the typing and the busywork. I do the thinking and make the calls.',
    link: { label: 'Read the framework', href: externalLinks.framework, external: true },
  },
  {
    title: 'The End-to-End Workflow',
    summary: 'Every trigger, node, skill, and agent in my framework wired as one live, n8n-style graph, from a fresh prompt to a verified deploy.',
    link: { label: 'Explore the workflow', href: externalLinks.workflow, external: true },
  },
]

/* ---------- Funnels and sites (self-contained demo pages in public/demos) ---------- */

const funnelStep = (file: string, label: string, tag: string, summary: string): Demo => ({
  id: file,
  label,
  tag,
  summary,
  href: `/demos/funnels/${file}.html`,
  thumb: { src: `/images/thumbs/${file}.webp`, alt: `${label} page preview`, width: 400, height: 225 },
})

const sampleSite = (file: string, label: string, summary: string): Demo => ({
  id: file,
  label,
  tag: 'Website',
  summary,
  href: `/demos/sites/${file}.html`,
  thumb: { src: `/images/thumbs/${file}.webp`, alt: `${label} site preview`, width: 400, height: 533 },
})

export const challengeFunnel: Demo[] = [
  funnelStep('sales-page', 'Sales Page', 'Top of funnel', 'Hooks visitors with a transformation promise, social proof, and a clear CTA.'),
  funnelStep('optin-free', 'Free Opt-in', 'Lead capture', 'Name and email in exchange for free 7-day challenge access.'),
  funnelStep('optin-paid', 'Paid Opt-in', 'Lead capture', 'Premium enrollment with value stacking and a streamlined payment flow.'),
  funnelStep('checkout', 'Checkout', 'Conversion', 'Order summary and payment with trust badges and fast form fields.'),
  funnelStep('confirmation-free', 'Free Confirmation', 'Thank you', 'Next steps and a community CTA to reduce early drop-off.'),
  funnelStep('confirmation-paid', 'Paid Confirmation', 'Thank you', 'Dashboard access, kickstarter call CTA, and an onboarding checklist.'),
  funnelStep('booking-kickstarter', 'Kickstarter Call', 'Appointment', 'Day-1 strategy call booking that sets expectations.'),
  funnelStep('booking-midchallenge', 'Mid-Challenge Check-in', 'Appointment', 'Day-4 check-in to keep momentum and prevent drop-off.'),
  funnelStep('booking-postchallenge', 'Post-Challenge Call', 'Appointment', 'A results review call, the natural upsell moment.'),
]

export const bookingPages: Demo[] = [
  funnelStep('real-estate-booking', 'Real Estate', 'Real estate', 'Property booking with agent photo and a viewing request form.'),
  funnelStep('hvac-booking', 'HVAC Services', 'HVAC', 'Seasonal urgency, trust signals, and fast mobile scheduling.'),
  funnelStep('dentistry-booking', 'Dental Clinic', 'Dentistry', 'FAQs, an insurance callout, and a two-step appointment form.'),
  funnelStep('coaching-booking', 'Coaching', 'Coaching', 'Coach bio, transformation stories, and a short discovery call form.'),
  funnelStep('retail-booking', 'Moving Company', 'Moving', 'Move stats, a 4-step process, and an estimate request form.'),
]

export const sampleSites: Demo[] = [
  sampleSite('real-estate-B-tech-luxury-scroll', 'Luxury Real Estate', 'Cinematic scroll, parallax reveals, and an editorial type system.'),
  sampleSite('moving-company-D-coastal-team', 'Coastal Movers', 'Process steps, crew photos, and a fast estimate request.'),
  sampleSite('lawfirm-D-lora-gold-small-town-solo', 'Solo Law Firm', 'Gold and serif: trust, credentials, and a clear consultation CTA.'),
  sampleSite('barbershop-E-brand-store', 'Barbershop Brand', 'Bookings, a product grid, and a confident street look.'),
  sampleSite('auto-repair-B-european-import', 'European Auto Import', 'Marque badges, a service menu, and an engineered visual language.'),
  sampleSite('auto-repair-E-performance-tuning', 'Performance Tuning', 'Dyno numbers, a build gallery, and a motorsport layout.'),
  sampleSite('marketing-agency-C-brand-studio', 'Brand Studio', 'Case studies, a services rail, and bold editorial motion.'),
  sampleSite('physical-therapy-E-cash-pay', 'Cash-Pay Physical Therapy', 'Plain-English pricing and a low-friction first-visit booking.'),
  sampleSite('solar-helios', 'Commercial Solar', 'Asset stats and a site-survey request flow.'),
  sampleSite('vacation-rental-management', 'Vacation Rental Mgmt', 'A multi-step slider and a property-onboarding CTA.'),
  sampleSite('utility-contractor', 'Utility Contractor', 'Divisions, a safety record, and a request-for-proposal entry point.'),
]

/* ---------- Apps and extensions ---------- */

export const apps: AppBuild[] = [
  {
    id: 'pickles',
    name: 'Pickles',
    kind: 'Android app',
    tagline: 'Filipino grocery tracker with budget control.',
    summary: 'Track prices across PH supermarkets, set per-trip budgets, and build a running product catalog. On Google Play, in beta testing.',
    status: 'Beta',
    image: { src: '/images/apps/pickles.webp', alt: 'Pickles on the Google Play Store', width: 540, height: 1200 },
  },
  {
    id: 'celery-music',
    name: 'Celery Music',
    kind: 'Android app',
    tagline: 'Your playlists, your vibe, your device.',
    summary: 'Import music, pull tracks from YouTube, build playlists, and tune with a 10-band EQ. No ads, no cloud, no tracking.',
    status: 'Beta',
    image: { src: '/images/apps/celery-music.webp', alt: 'Celery Music app screens', width: 480, height: 257 },
  },
  {
    id: 'lettuce-read',
    name: 'Lettuce Read',
    kind: 'Android app',
    tagline: 'Your purrfect reading companion.',
    summary: 'A free ebook reader for 21 file formats with no ads, no tracking, and no internet required.',
    status: 'Free',
    image: { src: '/images/apps/lettuce-read.webp', alt: 'Lettuce Read app screens', width: 480, height: 266 },
  },
  {
    id: 'ghl-key',
    name: 'BrewedOps GHL Key',
    kind: 'Chrome extension',
    tagline: 'The key behind every audit I run.',
    summary: 'Grabs the GoHighLevel token from a sub-account login so my backend tools can read and build through the API.',
    image: { src: '/images/apps/ghl-key.webp', alt: 'BrewedOps GHL Key extension popup', width: 419, height: 597 },
  },
  {
    id: 'yt2text',
    name: 'BrewedYT2Text',
    kind: 'Chrome extension',
    tagline: 'Any YouTube transcript in one click.',
    summary: 'Pulls the full transcript from a YouTube video, ready to drop into a doc, a prompt, or a repurposing flow.',
    image: { src: '/images/apps/yt2text.webp', alt: 'BrewedYT2Text extension popup', width: 403, height: 306 },
  },
]

/* ---------- AI agents and chatbots ---------- */

export const aiGroups: AiGroup[] = [
  {
    title: 'Agents',
    summary: 'They pick their own tools and finish the job.',
    builds: [
      { name: 'Hermes', summary: 'My own agent on a server, reachable from Telegram. Remembers, reads my notes, runs jobs unasked.', stack: 'Nous hermes-agent, extended by me, Docker on my VPS', status: 'Live' },
      { name: 'KapeSEO', summary: 'A keyword in, a long-form article out. It grades itself before handing over.', stack: 'Claude Code skill + subagent', status: 'Internal' },
      { name: 'KAPE Reel worker', summary: 'One Telegram message. It scripts, voices, captions, renders, and returns for approval.', stack: 'Node worker, ElevenLabs, Whisper, Remotion', status: 'Internal' },
      { name: 'BrewedOps OS roster', summary: 'Specialist agents with one job each, from GHL audits to client deliverables. Every write is approval-gated.', stack: 'Claude Code subagents', status: 'Internal' },
    ],
  },
  {
    title: 'RAG chatbots',
    summary: 'They search real documents first, then answer from what they found.',
    builds: [
      { name: 'Ken AI Bot', summary: 'Searches a client team library and live sales figures. Refuses to guess.', stack: 'Claude, pgvector search, 5 tools', status: 'Live' },
      { name: 'Kape AI knowledge search', summary: 'Pulls the best passages from my knowledge base, then answers.', stack: 'SQLite FTS5 ranking, Claude Haiku', status: 'Live' },
    ],
  },
  {
    title: 'Chatbots on BrewedOps.com',
    summary: 'Each does one job well.',
    builds: [
      { name: 'Workflow Creator', summary: 'Describe a GoHighLevel workflow in plain English, get the diagram back.', stack: 'Claude Sonnet', status: 'Live' },
      { name: 'Voice Call Tester', summary: 'Hold a real spoken call with your voice agent before a customer does.', stack: 'Claude Sonnet, ElevenLabs speech', status: 'Live' },
      { name: 'Copy Website', summary: 'Point it at a page, get an editable rebuild streamed back.', stack: 'Claude Sonnet, streaming', status: 'Live' },
    ],
  },
]

/** Number of builds on the Work page, shown as the sidebar badge. Derived, never typed in. */
export const workCount =
  1 + challengeFunnel.length + bookingPages.length + sampleSites.length + apps.length + aiGroups.reduce((n, g) => n + g.builds.length, 0)
